import { LitElement, html } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { GzFormFieldWebComponent } from '../../../ui/gzForm/formField';
import { RequiredValidator } from '../../../ui/gzForm/validators/required.validator';
import { FormGroup } from '../../../ui/gzForm/formGroup/form-group';
import { Subscription } from 'rxjs';
import { GzButtonWebComponent } from '../../../ui/gzButton';
import { ExclusionItemFormEvents } from '../../exclusionItemForm/types';
import { GzInputFileWebComponent } from '../../../ui/gzInputFile';
import { InputWidthValues } from '../../../ui/gzInput';
import { MaxLengthValidator } from '../../../ui/gzForm/validators/max-length.validator';

export class ExclusionListFormWebComponent extends LitElement {
  @property({ type: String, reflect: true })
  infoString = '';

  @property({ type: String, reflect: true })
  titleInputLabel = '';

  @property({ type: String, reflect: true })
  titleInputPlaceholder = '';

  @property({ type: String, reflect: true })
  descriptionInputLabel = '';

  @property({ type: String, reflect: true })
  descriptionValidationMsg = '';

  @property({ type: String, reflect: true })
  nameValidationMsg = '';

  @property({ type: String, reflect: true })
  descriptionInputPlaceholder = '';

  @property({ type: String, reflect: true })
  summary = '';

  @property({ type: String, reflect: true })
  importBtnString = '';

  @property({ type: String, reflect: true })
  clearBtnString = '';

  @property({ type: Boolean, attribute: true, reflect: true })
  hasManageRights = false;

  @query('#titleInput')
  titleInput!: GzFormFieldWebComponent;

  @query('#descriptionInput')
  descriptionInput!: GzFormFieldWebComponent;

  @query('#clearButton')
  clearButton: GzButtonWebComponent;

  @query('#importButton')
  importButton: GzFormFieldWebComponent;

  @state()
  formGroup: FormGroup | null;

  @state()
  private subscription: Subscription | null;

  static get styles() {
    return styles;
  }

  firstUpdated(changedProperties) {
    super.firstUpdated(changedProperties);
    this.createFormGroup();
  }

  updated(changedProperties) {
    if (
      (changedProperties.has('descriptionValidationMsg') && this.descriptionValidationMsg) ||
      (changedProperties.has('nameValidationMsg') && this.nameValidationMsg)
    ) {
      this.setFormValidators();
    }
  }

  render() {
    return html`
      <div class="exclusion-form">
        <div class="exclusion-form--row mb-1">
          <div class="exclusion-form--field">
            <gz-form-field id="titleInput" required>
              <gz-input
                gz-form-input
                inputWidth="${InputWidthValues.XLARGE}"
                label="${this.titleInputLabel}"
                placeholder="${this.titleInputPlaceholder}"
              ></gz-input>
              <gz-input-error></gz-input-error>
            </gz-form-field>
          </div>
          <gz-title fontsize="12" class="pt-1 info-text">${this.infoString}</gz-title>
        </div>

        <div class="exclusion-form--row d-flex flex-row align-items-stretch">
          <div class="exclusion-form--field">
            <gz-form-field id="descriptionInput" required>
              <gz-input
                gz-form-input
                inputWidth="${InputWidthValues.XLARGE}"
                label="${this.descriptionInputLabel}"
                placeholder="${this.descriptionInputPlaceholder}"
              ></gz-input>
              <gz-input-error></gz-input-error>
            </gz-form-field>
          </div>

          <div class="exclusion-form--actions">
            ${this.renderImportBtn()}
            <gz-button style="display:none" type="quiet" color="secondary" id="clearButton" disabled>
              ${this.clearBtnString}
            </gz-button>
          </div>

          <div class="exclusion-form--summary d-flex align-items-center"><span id="summary">${this.summary}</span></div>
        </div>
      </div>
    `;
  }

  renderImportBtn() {
    if (this.hasManageRights) {
      return html`
        <gz-file-input type="quiet" color="secondary" id="importFile" accept=".csv" @change="${this.importAction}">
          <gz-button browse-button type="full" color="primary">${this.importBtnString}</gz-button>
        </gz-file-input>
      `;
    } else {
      return html`<gz-button browse-button type="quiet" color="secondary" disabled>${this.importBtnString}</gz-button>`;
    }
  }

  importAction(): void {
    const input = this.shadowRoot?.querySelector('gz-file-input') as GzInputFileWebComponent;

    if (!input) {
      return;
    }

    const file = input.getFormValue();

    if (!file) {
      return;
    }

    this.dispatchEvent(
      new CustomEvent(ExclusionItemFormEvents.IMPORT_EXCLUSIONS, {
        detail: {
          file: file,
        },
        bubbles: true,
        composed: true,
      })
    );

    input.setFormValue(null);
  }

  disconnectedCallback(): void {
    this.formGroup?.unsubscribe();
    this.subscription?.unsubscribe();
    super.disconnectedCallback();
  }

  private setFormValidators() {
    this.titleInput.Validators = [MaxLengthValidator(64, this.nameValidationMsg), RequiredValidator('')];

    this.descriptionInput.Validators = [MaxLengthValidator(128, this.descriptionValidationMsg)];
  }

  private createFormGroup() {
    this.setFormValidators();

    this.formGroup = new FormGroup({
      title: this.titleInput,
      description: this.descriptionInput,
    });

    this.subscription = this.formGroup.valid$.subscribe({
      next: valid => {
        this.clearButton.disabled = !valid;
      },
    });
  }
}
